// FLAGS: none
// Paginated list of the Workplace Sessions a client has joined.
import { apiPaths, apiPathsWithParams, pageSize } from "@src/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fiveMinutes } from "@src/queries/staletimes";
import { Spaces } from "@src/api/v1/Spaces";
import {
  GetHostedSessionsParams,
  ResponseConveneHostedSessionResponsePaginated,
  ResponseSpacesGetClientSpaceResponsePaginated,
} from "@src/api/v1/data-contracts";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";

export const useGetClientSpacesQuery = (
  clientId: string | undefined,
  page: number = 0,
  limit: number = pageSize,
) =>
  useQuery<ResponseSpacesGetClientSpaceResponsePaginated>({
    queryKey: [apiPathsWithParams.getClientSpaces(clientId!), page, limit],
    queryFn: () =>
      new Spaces(OpenUpHttpClient).getClientSpaces({
        clientId: clientId!,
        page,
        limit,
      }),
    enabled: !!clientId,
    staleTime: fiveMinutes,
  });

// `enabled` lets a caller list only 1:1 sessions without firing this request, for a status facet
// that hosted spaces cannot express.
export const useHostedSpacesQuery = (
  params: GetHostedSessionsParams,
  enabled: boolean = true,
) =>
  useQuery<ResponseConveneHostedSessionResponsePaginated>({
    queryKey: [apiPaths.hostedSpaces, params],
    queryFn: () => new Spaces(OpenUpHttpClient).getHostedSessions(params),
    enabled,
    staleTime: fiveMinutes,
  });

// The returned link embeds a short-lived Zoom token — request it per click, never cache or store.
export const useSpaceStartLinkMutation = () =>
  useMutation({
    mutationFn: (spaceId: string) =>
      new Spaces(OpenUpHttpClient).getStartLink(spaceId),
  });
